import React, { ReactNode, RefObject, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  useFloating,
  size,
  offset,
  FloatingFocusManager,
  useDismiss,
  autoUpdate
} from '@floating-ui/react';

import { useBitnetSearchContext } from '../context/BitNetSearchContext';

import { Select } from './Select';
import { IDeceasedContact } from '~/types/IDeceasedContact';

const DropdownMenuWrapper = styled.div`
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.white};
  z-index: 2;
  max-height: 324px;
  width: 100%;
  overflow: scroll;
  border: 1px solid ${({ theme }) => theme.colors.lightDust};
  border-radius: 4px;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 11px;
`;

interface Props {
  onSelect: (selected: IDeceasedContact | null) => void;
  children: (inputRef: RefObject<HTMLInputElement>) => ReactNode;
  show: boolean;
  close: () => void;
  id: string;
}

export function DropdownMenu(props: Props) {
  const { children, onSelect, show, close, id } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    loading,
    data,
    openModal,
    months,
    loadMore,
    value
  } = useBitnetSearchContext();

  const {
    refs,
    floatingStyles,
    context,
    elements,
    update
  } = useFloating<HTMLInputElement>({
    transform: false,
    placement: 'bottom-start',
    open: show,
    onOpenChange(open, event, reason) {
      if (reason === 'escape-key' || reason === 'outside-press') {
        close();
      }
    },
    middleware: [
      offset(8),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${rects.reference.width}px`
          });
        }
      })
    ],
    elements: {
      reference: inputRef?.current
    }
  });

  useEffect(() => {
    if (show && elements.reference && elements.floating) {
      const cleanup = autoUpdate(elements.reference, elements.floating, update);
      return cleanup;
    }
  }, [show, elements, update]);

  useDismiss(context, {
    capture: true,
    outsidePress: true,
    escapeKey: true
  });

  return (
    <>
      {children(inputRef)}
      {show && (
        <FloatingFocusManager
          context={context}
          modal={true}
          order={['reference', 'content']}
        >
          <DropdownMenuWrapper ref={refs.setFloating} style={floatingStyles}>
            <Select
              data={data}
              loading={loading}
              onSelect={onSelect}
              loadMore={months < 3 ? loadMore : null}
              openModal={openModal}
              closeMenu={() => {
                inputRef.current?.focus();
                close();
              }}
              id={id}
              value={value}
            />
          </DropdownMenuWrapper>
        </FloatingFocusManager>
      )}
    </>
  );
}
